import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import MobileFrame from '../components/MobileFrame';
import axios from 'axios';
import { set } from 'react-hook-form';


const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://staging.api.pay.cleverpe.com/api';


const Login: React.FC = () => {
  const [phoneNumber, setPhoneNumber] = useState('');
  const [dob, setDob] = useState(''); // Remove hardcoded DOB value
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [panNumber, setPanNumber] = useState('');
  const [income, setIncome] = useState<number>(0);
  const [employmentType, setEmploymentType] = useState('1'); // Default to '1' (Salaried)
  const [isConsent1Checked, setIsConsent1Checked] = useState(false);
  const [isConsent2Checked, setIsConsent2Checked] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  // const [isKeyboardOpen, setIsKeyboardOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [transactionData, setTransactionData] = useState<any>(null);
  const [partnerMobileNumber, setPartnerMobileNumber] = useState<string>('');
  const [partnerName, setPartnerName] = useState<string>('');
  const [schemeSelected, setSchemeSelected] = useState<string>('');
  const [isPhoneNumberPrefilled, setIsPhoneNumberPrefilled] = useState(false);
  const phoneInputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Parse query parameters to get token and mobile number
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const token = params.get('token');
    const mobileNumber = params.get('mobileNumber');
    const schemeParam = params.get('schemeSelected');

    // Store scheme selected if available in URL parameters
    if (schemeParam) {
      setSchemeSelected(schemeParam);
      sessionStorage.setItem('schemeSelected', schemeParam);
    }

    // If token is provided, validate it
    if (token) {
      fetchTransactionData(token);
    }

    // Pre-fill mobile number if provided
    if (mobileNumber) {
      setPhoneNumber(formatPhoneNumber(mobileNumber));
      setIsPhoneNumberPrefilled(true);
    }
  }, [location.search]);

  // Parse query parameters to get token and mobile number
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const token = params.get('token');
    const mobileNumber = params.get('mobileNumber');
    const schemeParam = params.get('schemeSelected');

    // Store scheme selected if available in URL parameters
    if (schemeParam) {
      setSchemeSelected(schemeParam);
      sessionStorage.setItem('schemeSelected', schemeParam);
    }

    // If token is missing → redirect to NotFound page
    if (!token) {
      navigate('/not-found', { replace: true });
      return;
    }

    // If token exists, fetch transaction data
    fetchTransactionData(token);

    // Pre-fill mobile number if provided
    if (mobileNumber) {
      setPhoneNumber(formatPhoneNumber(mobileNumber));
      setIsPhoneNumberPrefilled(true);
    }
  }, [location.search]);

  // Function to fetch transaction data from token
  const fetchTransactionData = async (token: string) => {
    try {
      setIsLoading(true);
      const response = await axios.get(`${API_BASE_URL}/gateway/login`, {
        params: { token }
      });

      if (response.data.success) {
        const data = response.data.data;
        sessionStorage.setItem('token', token);
        setTransactionData(data);

        // Pre-fill mobile number if it's in the response
        if (data.mobileNo) {
          setPhoneNumber(formatPhoneNumber(data.mobileNo));
          setIsPhoneNumberPrefilled(true);
        }

        // Store partner mobile number if available in the response
        if (data.partnerMobileNumber) {
          setPartnerMobileNumber(data.partnerMobileNumber);
          setPartnerName(data.partnerName || '');
          sessionStorage.setItem('partnerMobileNumber', data.partnerMobileNumber);
          sessionStorage.setItem('partnerName', data.partnerName || '');
          sessionStorage.setItem('callbackUrl', data.callbackUrl || '');
        }

        // Store scheme selected if available in the response data
        if (data.schemeSelected) {
          setSchemeSelected(data.schemeSelected);
          sessionStorage.setItem('schemeSelected', data.schemeSelected);
        }
      } else {
        // Invalid token / transaction → redirect to NotFound
        navigate('/not-found', { replace: true });
      }
    } catch (err) {
      // Any fetch error → redirect to NotFound
      navigate('/not-found', { replace: true });
    } finally {
      setIsLoading(false);
    }
  };

  // Format date to YYYY-MM-DD if it's not already in that format
  const formatDateToYYYYMMDD = (dateString: string) => {
    // If it's already in YYYY-MM-DD format, return as is
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      return dateString;
    }

    // Try to parse various date formats
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        // If date is invalid, check if it's in DD/MM/YYYY format
        const parts = dateString.split(/[\/\-\.]/);
        if (parts.length === 3) {
          // Try DD/MM/YYYY
          const day = parseInt(parts[0]);
          const month = parseInt(parts[1]) - 1;
          const year = parseInt(parts[2]);
          const newDate = new Date(year, month, day);
          if (!isNaN(newDate.getTime())) {
            return newDate.toISOString().slice(0, 10);
          }
        }
        // Return default if nothing works
        return dob;
      }
      return date.toISOString().slice(0, 10);
    } catch (e) {
      return dob;
    }
  };

  // Focus the input field when component mounts
  useEffect(() => {
    if (phoneInputRef.current) {
      phoneInputRef.current.focus();
    }
  }, []);

  // Handle window resize to determine if mobile view should be shown
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Detect keyboard open on mobile
  // useEffect(() => {
  //   if (!isMobile) return;

  //   const visualViewport = window.visualViewport;
  //   if (!visualViewport) return;

  //   const handleVisualViewportResize = () => {
  //     const isKeyboard = visualViewport.height < window.innerHeight * 0.75;
  //     setIsKeyboardOpen(isKeyboard);
  //   };

  //   visualViewport.addEventListener('resize', handleVisualViewportResize);
  //   return () => visualViewport.removeEventListener('resize', handleVisualViewportResize);
  // }, [isMobile]);

  // Add this inside your Login component, near other useEffects

  useEffect(() => {
    const handleEnterKey = (e: KeyboardEvent) => {
      if (e.key === 'Enter') {
        handleContinue();
      }
    };

    window.addEventListener('keydown', handleEnterKey);

    return () => {
      window.removeEventListener('keydown', handleEnterKey);
    };
  }, [phoneNumber, isConsent1Checked, isConsent2Checked, isLoading, transactionData, dob, name, email, panNumber, income, employmentType, partnerMobileNumber]);


  const handleContinue = async () => {
    setError('');
    const cleanPhone = phoneNumber.replace(/\s+/g, '');

    if (cleanPhone.length !== 10) {
      setError('Please enter a valid 10-digit mobile number');
      return;
    }

    try {
      setIsLoading(true);

      const payload = {
        mobileNumber: cleanPhone,
      };

      const response = await axios.post(`${API_BASE_URL}/auth/login`, payload);

      if (response.data.success) {
        // Store mobile and basic values
        sessionStorage.setItem('mobileNumber', cleanPhone);
        if (response.data.expiresAt) {
          sessionStorage.setItem('otpExpiresAt', response.data.expiresAt.toString());
        }
        // console.log('data received from login API:', response.data);

        // Use transactionData (already set from /gateway/login) as the primary source
        const data = transactionData || {}; // fallback if null

        const finalTransactionData = {
          orderId: data.orderId || '',
          lender: data.lender || '',
          partnerId: data.partnerId || '',
          zypeVersion: data.zypeVersion || '',
          amount: data.amount || '',
          mobileNumber: cleanPhone,
          dob: data.dob || dob || '',
          pincode: data.pincode || '',
          name: data.name || name || '',
          email: data.email || email || '',
          panNumber: data.panNumber || panNumber || '',
          income: data.income || income || '',
          employmentType: data.employmentType || employmentType || '',
          partnerMobileNumber: data.partnerMobileNumber || partnerMobileNumber || '',
          partnerName: data.partnerName || '',
        };
        console.log('Final transactionData:', finalTransactionData);

        // Store individual fields
        sessionStorage.setItem('dob', finalTransactionData.dob);
        sessionStorage.setItem('pincode', finalTransactionData.pincode);
        sessionStorage.setItem('name', finalTransactionData.name);
        sessionStorage.setItem('email', finalTransactionData.email);
        sessionStorage.setItem('panNumber', finalTransactionData.panNumber);
        sessionStorage.setItem('income', finalTransactionData.income.toString());
        sessionStorage.setItem('employmentType', finalTransactionData.employmentType);
        sessionStorage.setItem('partnerMobileNumber', finalTransactionData.partnerMobileNumber);
        sessionStorage.setItem('partnerName', finalTransactionData.partnerName);
        sessionStorage.setItem('orderId', finalTransactionData.orderId);
        sessionStorage.setItem('lender', finalTransactionData.lender);
        sessionStorage.setItem('zypeVersion', finalTransactionData.zypeVersion);

        // Store entire object
        sessionStorage.setItem('transactionData', JSON.stringify(finalTransactionData));
        // console.log('Final transactionData stored in sessionStorage:', finalTransactionData);
        // console.log("transactionData after final:", transactionData);

        navigate('/otp');
      } else {
        setError(response.data.message || 'Failed to send OTP. Please try again.');
      }
    } catch (err: any) {
      // console.error('Login error:', err);
      setError(err.response?.data?.message || 'Failed to send OTP. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const formatPhoneNumber = (value: string) => {
    // Only allow numbers
    const digits = value.replace(/\D/g, '');
    // Format as XXX XXX XXXX
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `${digits.slice(0, 3)} ${digits.slice(3)}`;
    if (digits.length <= 10) return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6, 10)}`;
    return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6, 10)}`;
  };

  // Handle key events to prevent losing focus
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && phoneNumber.length > 0) {
      // Don't prevent default - let the normal backspace behavior work
      // We'll handle the formatting after the change in a useEffect
    }
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target.value;
    const digits = input.replace(/\D/g, '');

    // Format and limit to 10 digits
    if (digits.length <= 10) {
      const formatted = formatPhoneNumber(digits);

      // Store the current cursor position and selection before updating state
      const cursorPosition = e.target.selectionStart || 0;
      const selectionEnd = e.target.selectionEnd || 0;
      const isAtEnd = cursorPosition === input.length;

      // Update the state without triggering re-renders that cause focus loss
      setPhoneNumber(formatted);

      // Don't use requestAnimationFrame on mobile as it can cause focus issues
      if (!isMobile) {
        // On desktop, we can safely use this approach
        requestAnimationFrame(() => {
          if (phoneInputRef.current) {
            if (document.activeElement !== phoneInputRef.current) {
              phoneInputRef.current.focus();
            }

            if (isAtEnd) {
              phoneInputRef.current.setSelectionRange(formatted.length, formatted.length);
            } else {
              const spacesBefore = (input.substring(0, cursorPosition).match(/ /g) || []).length;
              const spacesInFormatted = (formatted.substring(0, cursorPosition + (formatted.length - input.length)).match(/ /g) || []).length;
              const spaceDiff = spacesInFormatted - spacesBefore;

              const newCursorPos = cursorPosition + spaceDiff;

              if (newCursorPos >= 0 && newCursorPos <= formatted.length) {
                phoneInputRef.current.setSelectionRange(newCursorPos, newCursorPos);
              }
            }
          }
        });
      }
    }
  };
  


  const LoginContent = () => (
    <div className="flex flex-col h-full">
      {/* Page indicator only */}
      <div className="flex items-center justify-end mb-4">
        <div className="text-base font-semibold text-gray-700 dark:text-gray-300">1<span className="font-normal text-gray-400 dark:text-gray-500">/3</span></div>
      </div>

      {/* Main content */}
      <div className="flex-1">
        <h1 className="text-[1.6rem] font-extrabold mb-4 text-gray-900 dark:text-white">Verify Mobile Number</h1>

        {error && (
          <div className="p-3 mb-4 text-sm text-red-700 border border-red-200 rounded-lg dark:text-red-400 dark:border-red-900 bg-red-50 dark:bg-red-900/20">
            {error}
          </div>
        )}

        <div className="mb-6">
          <label htmlFor="phone" className="block text-[0.98rem] text-gray-500 dark:text-gray-400 mb-2 font-medium">Phone Number</label>
          <input
            id="phone"
            type="tel"
            value={phoneNumber}
            readOnly
            disabled
            className="w-full p-2 text-lg text-gray-900 border border-gray-200 rounded-xl bg-gray-100 dark:bg-dark-card dark:text-white opacity-80"
          />

          {isPhoneNumberPrefilled && (
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Mobile number is pre-filled and cannot be changed
            </p>
          )}
        </div>

        <div className="space-y-4 mb-4">
          <div className="flex items-start gap-2">
            <input
              type="checkbox"
              id="consent1"
              checked={isConsent1Checked}
              onChange={() => setIsConsent1Checked(!isConsent1Checked)}
              className="mt-1 w-4 h-4 border border-gray-300 dark:border-gray-600 rounded-[4px] accent-gray-700 dark:accent-app-blue"
              disabled={isLoading}
            />
            <label htmlFor="consent1" className="text-xs leading-tight text-gray-900 dark:text-gray-200">
              I consent to CleverPe conducting a pre-approval check for my credit eligibility, including sharing my personal and financial data with credit bureaus or other authorized institutions as required by RBI and other regulatory bodies.
            </label>
          </div>

          <div className="flex items-start gap-2">
            <input
              type="checkbox"
              id="consent2"
              checked={isConsent2Checked}
              onChange={() => setIsConsent2Checked(!isConsent2Checked)}
              className="mt-1 w-4 h-4 border border-gray-300 dark:border-gray-600 rounded-[4px] accent-gray-700 dark:accent-app-blue"
              disabled={isLoading}
            />
            <label htmlFor="consent2" className="text-xs leading-tight text-gray-900 dark:text-gray-200">
              I understand that this information will be used solely for validating my eligibility for financial products/services and to evaluate my creditworthiness.
            </label>
          </div>
        </div>
      </div>

      {/* Footer section - conditionally displayed based on keyboard state */}
      <div className="pt-2">
        {/* Footer text - hide when keyboard is open on mobile */}
        <div className="px-2 mb-2 text-xs text-center text-gray-700 dark:text-gray-400">
          By continuing, you agree to CleverPe{' '}
          <a
            href="https://cleverpe.com/termsandconditions"
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 underline dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
          >
            Terms and Conditions
          </a>{' '}
          of Use and{' '}
          <a
            href="https://cleverpe.com/privacypolicy"
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 underline dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
          >
            Privacy Policy
          </a>
          . ©{new Date().getFullYear()} Cleverpe Technologies Private Limited
        </div>


        {/* Powered by CleverPe - hide when keyboard is open on mobile */}
        <div className="flex items-center justify-center mb-4">
          <img
            src="/CleverPeLogos/PoweredBy.svg"
            alt="Powered By CleverPe"
            className="h-10"
            onError={(e) => {
              e.currentTarget.src = 'https://placehold.co/140x40?text=Powered+By+CleverPe';
            }}
          />
        </div>


        {/* Continue button */}
        <button
          onClick={handleContinue}
          disabled={isLoading || !phoneNumber || !isConsent1Checked || !isConsent2Checked}
          className={`w-full py-3 rounded-full text-center font-semibold text-lg transition
            ${(isLoading || !phoneNumber || !isConsent1Checked || !isConsent2Checked)
              ? 'bg-gray-200 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed'
              : 'bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 shadow-md active:scale-[0.97]'}`}
        >
          {isLoading ? 'Please Wait...' : 'Continue'}
        </button>
      </div>
    </div>
  );

  return isMobile ? (
    <div className="h-[100dvh] w-full p-5 overflow-hidden bg-white dark:bg-dark">
      <LoginContent />
    </div>
  ) : (
    <MobileFrame>
      <LoginContent />
    </MobileFrame>
  );

};

export default Login;